import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../context/AuthContext';
import { getMembershipTiers, getMyMembership, subscribeMembership, cancelMembership } from '../services/api';
import { Button } from '../components/ui/button';
import { Crown, Check, Zap, Shield } from 'lucide-react';
import { toast } from 'sonner';

const MembershipPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [tiers, setTiers] = useState({});
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState('');

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) { navigate('/login'); return; }
    const fetch = async () => {
      try {
        const [t, m] = await Promise.all([getMembershipTiers(), getMyMembership()]);
        setTiers(t);
        setCurrent(m);
      } catch {} finally { setLoading(false); }
    };
    fetch();
  }, [isAuthenticated, authLoading, navigate]);

  const handleSubscribe = async (tier) => {
    setSubscribing(tier);
    try {
      const res = await subscribeMembership(tier);
      toast.success(res.message);
      const m = await getMyMembership();
      setCurrent(m);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally { setSubscribing(''); }
  };

  const handleCancel = async () => {
    try {
      const res = await cancelMembership();
      toast.success(res.message);
      const m = await getMyMembership();
      setCurrent(m);
    } catch { toast.error('Failed to cancel'); }
  };

  if (loading) return <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center"><div className="animate-pulse text-[#596652]">Loading...</div></div>;

  const tierOrder = ['free', 'plus', 'pro'];
  const tierIcons = { free: Shield, plus: Zap, pro: Crown };
  const tierColors = { free: '#8C9A76', plus: '#D97757', pro: '#F59E0B' };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Helmet>
        <title>Membership Plans | NeighborBorrow</title>
        <meta name="description" content="Upgrade your NeighborBorrow membership for lower fees, more listings, and featured item slots. Free, Plus, and Pro plans available." />
      </Helmet>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-[#2C3329] mb-3">Membership Plans</h1>
          <p className="text-[#596652] max-w-lg mx-auto">Lower fees, more listings, and featured slots. Upgrade to get more from NeighborBorrow.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {tierOrder.map((key) => {
            const tier = tiers[key];
            if (!tier) return null;
            const Icon = tierIcons[key];
            const color = tierColors[key];
            const isCurrent = current?.tier === key;

            return (
              <div key={key} className={`bg-white rounded-2xl border-2 p-6 transition-all ${isCurrent ? 'border-[' + color + '] shadow-lg' : 'border-[#EBE8DF]'}`} data-testid={`tier-${key}`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: color + '20' }}>
                    <Icon className="w-5 h-5" style={{ color }} />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-lg text-[#2C3329]">{tier.name}</h3>
                    {isCurrent && <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-[#4CAF50]/10 text-[#4CAF50]">Current</span>}
                  </div>
                </div>

                <div className="mb-6">
                  <span className="text-3xl font-heading font-bold text-[#2C3329]">${tier.price}</span>
                  {tier.price > 0 && <span className="text-[#596652] text-sm">/month</span>}
                </div>

                <ul className="space-y-3 mb-6 text-sm">
                  <li className="flex items-center gap-2 text-[#596652]">
                    <Check className="w-4 h-4 text-[#4CAF50]" />
                    <span><strong>{tier.fee_percent}%</strong> service fee</span>
                  </li>
                  <li className="flex items-center gap-2 text-[#596652]">
                    <Check className="w-4 h-4 text-[#4CAF50]" />
                    <span>Up to <strong>{tier.max_listings}</strong> listings</span>
                  </li>
                  <li className="flex items-center gap-2 text-[#596652]">
                    <Check className="w-4 h-4 text-[#4CAF50]" />
                    <span><strong>{tier.featured_slots}</strong> featured slots</span>
                  </li>
                </ul>

                {isCurrent ? (
                  key !== 'free' ? (
                    <Button variant="outline" onClick={handleCancel} className="w-full border-[#EBE8DF] rounded-full text-[#596652]" data-testid={`cancel-${key}`}>
                      Cancel Plan
                    </Button>
                  ) : (
                    <Button disabled className="w-full rounded-full bg-[#EBE8DF] text-[#596652]">Current Plan</Button>
                  )
                ) : (
                  <Button
                    onClick={() => handleSubscribe(key)}
                    disabled={!!subscribing || key === 'free'}
                    className="w-full rounded-full text-white"
                    style={{ backgroundColor: color }}
                    data-testid={`subscribe-${key}`}
                  >
                    {subscribing === key ? 'Subscribing...' : key === 'free' ? 'Free' : `Upgrade to ${tier.name}`}
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default MembershipPage;
